import React, { useState, useRef, useEffect } from "react";
import Input from "antd/es/input/Input";
import { Button, Typography, Select } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { GreenNotify, RedNotify, uploadMediaToS3 } from "../../helper/helper";
import { callApi } from "../../api/apiCaller";
import routes from "../../api/routes";
import Loader from "../../components/loader/loader";
import { imagePlaceholder } from "../../assets";

const TICKER_TYPES = [
  { label: "Text", value: "text" },
  { label: "Image", value: "image" },
];

const EditTickerItem = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const item = location?.state?.item;

  const [type, setType] = useState(item?.type ?? "text");
  const [textValue, setTextValue] = useState(item?.type === "text" ? item?.value ?? "" : "");
  const [imageUrl, setImageUrl] = useState(item?.type === "image" ? item?.value ?? "" : "");
  const [imageFile, setImageFile] = useState(null);
  const [active, setActive] = useState(item?.active ?? true);
  const [isloading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!item) {
      RedNotify("No ticker item to edit");
      navigate("/ticker");
      return;
    }
    setType(item.type);
    setTextValue(item.type === "text" ? item.value ?? "" : "");
    setImageUrl(item.type === "image" ? item.value ?? "" : "");
    setActive(item.active ?? true);
  }, [item, navigate]);

  const updateTickerItem = () => {
    if (type === "text") {
      if (!textValue?.trim()) {
        RedNotify("Please enter text content");
        return;
      }
    } else {
      if (!imageUrl && !imageFile) {
        RedNotify("Please upload or keep existing image");
        return;
      }
    }

    const body = {
      type,
      value: type === "text" ? textValue.trim() : imageUrl,
      active,
    };

    const onSuccess = () => {
      GreenNotify("Ticker item updated successfully");
      navigate("/ticker");
    };

    const onError = (err) => {
      console.log("Error updating ticker item", err);
      RedNotify("Failed to update ticker item");
    };

    callApi("PATCH", `${routes.updateTickerItem}${item._id}`, body, setIsLoading, onSuccess, onError);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      RedNotify("Please select an image file");
      return;
    }
    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;
    setIsLoading(true);
    try {
      const url = await uploadMediaToS3(imageFile, false);
      setImageUrl(url);
      setImageFile(null);
      GreenNotify("Image uploaded successfully");
    } catch (error) {
      RedNotify("Image upload failed");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!item) return null;

  return (
    <div style={{ marginBottom: "2rem", padding: "3rem", width: "50rem" }}>
      <Loader loading={isloading} />
      <h2>Edit Ticker Item</h2>

      <Typography.Title level={5} style={{ marginTop: "2rem" }}>
        Type
      </Typography.Title>
      <Select
        value={type}
        onChange={(v) => {
          setType(v);
          if (v === "text") setImageUrl("");
          else setTextValue("");
          setImageFile(null);
        }}
        options={TICKER_TYPES}
        style={{ marginTop: "0.5rem", width: "100%", maxWidth: "20rem" }}
      />

      {type === "text" && (
        <>
          <Typography.Title level={5} style={{ marginTop: "2rem" }}>
            Text content
          </Typography.Title>
          <Input.TextArea
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder="Enter ticker text"
            style={{ marginTop: "0.5rem", minHeight: 100, width: "100%", maxWidth: "32rem" }}
            rows={3}
          />
        </>
      )}

      {type === "image" && (
        <>
          <Typography.Title level={5} style={{ marginTop: "2rem" }}>
            Image thumbnail
          </Typography.Title>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: "none" }}
          />
          <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: 80,
                height: 80,
                borderRadius: 8,
                border: "1px dashed #d9d9d9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
                background: "#fafafa",
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <img src={imagePlaceholder} alt="Upload" style={{ width: 40, height: 40, opacity: 0.5 }} />
              )}
            </div>
            {imageFile && (
              <Button type="primary" onClick={handleImageUpload}>
                Upload to server
              </Button>
            )}
          </div>
        </>
      )}

      <Typography.Title level={5} style={{ marginTop: "2rem" }}>
        Active
      </Typography.Title>
      <Select
        value={active}
        onChange={setActive}
        options={[
          { label: "Yes", value: true },
          { label: "No", value: false },
        ]}
        style={{ marginTop: "0.5rem", width: "100%", maxWidth: "10rem" }}
      />

      <div style={{ marginTop: "2rem", display: "flex", gap: 12 }}>
        <Button type="primary" onClick={updateTickerItem}>
          Save
        </Button>
        <Button onClick={() => navigate("/ticker")}>Cancel</Button>
      </div>
    </div>
  );
};

export default EditTickerItem;
